import React, { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { Modal } from "antd";
import SwiperCore, { Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { colors } from "../../utils/theme";
import { device } from "../../utils/breakpoints";
import StyledButton from "../StyledButton";
import LinkButton from "../LinkButton";
import StyleElement from "../StyleElement";
import { contact } from "../../urls";

SwiperCore.use([Pagination]);

const MUTED = "#5C6353";
const GLASS = "rgba(255,252,245,.62)";
const GLASS_HOVER = "rgba(255,252,245,.85)";
const GLASS_TAB = "rgba(255,252,245,.45)";
const GLASS_TAB_HOVER = "rgba(255,252,245,.8)";

const ProductRedesignPage = ({ product, onBook }) => {
  const { hero, marquee, usps, use_cases_title, use_cases, availability_cta } =
    product;

  const [activeUseCaseId, setActiveUseCaseId] = useState(use_cases?.[0]?.id);
  const activeUseCase =
    use_cases?.find((useCase) => useCase.id === activeUseCaseId) ||
    use_cases?.[0];

  // Een tab toont één afbeelding tegelijk; `images` mag er meerdere bevatten.
  const panelImages = activeUseCase?.images?.length
    ? activeUseCase.images
    : activeUseCase?.image
      ? [{ image: activeUseCase.image, alt: activeUseCase.image_alt }]
      : [];
  const [activePanelImage, setActivePanelImage] = useState(0);
  useEffect(() => setActivePanelImage(0), [activeUseCaseId]);
  const showPanelPrev = () =>
    setActivePanelImage(
      (index) => (index - 1 + panelImages.length) % panelImages.length,
    );
  const showPanelNext = () =>
    setActivePanelImage((index) => (index + 1) % panelImages.length);

  const heroGallery = [
    ...(hero.image ? [{ image: hero.image, alt: hero.image_alt }] : []),
    ...(hero.thumbnails || []),
  ];
  const [activeHeroImage, setActiveHeroImage] = useState(0);
  const [isHeroLightboxOpen, setIsHeroLightboxOpen] = useState(false);
  const showHeroPrev = () =>
    setActiveHeroImage(
      (index) => (index - 1 + heroGallery.length) % heroGallery.length,
    );
  const showHeroNext = () =>
    setActiveHeroImage((index) => (index + 1) % heroGallery.length);

  const HERO_THUMBS_VISIBLE = Math.min(3, heroGallery.length);
  const heroThumbWindowStart = Math.min(
    Math.max(activeHeroImage - 1, 0),
    Math.max(heroGallery.length - HERO_THUMBS_VISIBLE, 0),
  );
  const visibleHeroThumbs = heroGallery
    .map((image, index) => ({ ...image, index }))
    .slice(heroThumbWindowStart, heroThumbWindowStart + HERO_THUMBS_VISIBLE);

  const heroContent = (
    <div className={"container"}>
      <HeroGrid>
        <div>
          {!!hero.kicker && <Kicker>{hero.kicker}</Kicker>}
          <Title>
            {hero.title}
            {!!hero.highlight && <Highlight>{hero.highlight}</Highlight>}
            {hero.title_suffix}
          </Title>
          {hero.intro?.map((paragraph, index) => (
            <Intro key={index}>{paragraph}</Intro>
          ))}
          {!!hero.facts?.length && (
            <FactsStrip>
              {hero.facts.map((fact, index) => (
                <FactTile key={index}>
                  <FactValue>{fact.value}</FactValue>
                  <FactLabel>{fact.label}</FactLabel>
                </FactTile>
              ))}
            </FactsStrip>
          )}
          <ButtonRow>
            {!!hero.primary_cta && (
              <StyledButton type="primary" onClick={onBook}>
                {hero.primary_cta}
              </StyledButton>
            )}
            {!!hero.secondary_cta && (
              <LinkButton to={contact} small type={"outlined"}>
                {hero.secondary_cta}
              </LinkButton>
            )}
          </ButtonRow>
        </div>
        {!!heroGallery.length && (
          <HeroMedia>
            <MainImageWrap>
              <MainImage
                src={heroGallery[activeHeroImage]?.image}
                alt={heroGallery[activeHeroImage]?.alt}
              />
              <ZoomButton
                type={"button"}
                aria-label={"Bekijk foto groter"}
                onClick={() => setIsHeroLightboxOpen(true)}
              >
                <i className="feather-search" />
              </ZoomButton>
              {heroGallery.length > 1 && (
                <>
                  <HeroArrow
                    type={"button"}
                    $side={"left"}
                    aria-label={"Vorige foto"}
                    onClick={showHeroPrev}
                  >
                    <i className="feather-chevron-left" />
                  </HeroArrow>
                  <HeroArrow
                    type={"button"}
                    $side={"right"}
                    aria-label={"Volgende foto"}
                    onClick={showHeroNext}
                  >
                    <i className="feather-chevron-right" />
                  </HeroArrow>
                </>
              )}
            </MainImageWrap>
            {heroGallery.length > 1 && (
              <ThumbGrid>
                {visibleHeroThumbs.map((image) => (
                  <ThumbButton
                    key={image.index}
                    type={"button"}
                    aria-label={`Foto ${image.index + 1}`}
                    $active={image.index === activeHeroImage}
                    onClick={() => setActiveHeroImage(image.index)}
                  >
                    <Thumb src={image.image} alt={image.alt} />
                  </ThumbButton>
                ))}
              </ThumbGrid>
            )}
          </HeroMedia>
        )}
      </HeroGrid>
    </div>
  );

  return (
    <>
      {!!marquee?.length ? (
        <HeroWithMarquee>
          <HeroSection $fill>{heroContent}</HeroSection>
          <Marquee words={marquee} />
        </HeroWithMarquee>
      ) : (
        <HeroSection>{heroContent}</HeroSection>
      )}

      {!!heroGallery.length && (
        <Modal
          open={isHeroLightboxOpen}
          onCancel={() => setIsHeroLightboxOpen(false)}
          footer={null}
          centered
          width={960}
        >
          <LightboxImageWrap>
            <LightboxImage
              src={heroGallery[activeHeroImage]?.image}
              alt={heroGallery[activeHeroImage]?.alt}
            />
            {heroGallery.length > 1 && (
              <>
                <HeroArrow
                  type={"button"}
                  $side={"left"}
                  aria-label={"Vorige foto"}
                  onClick={showHeroPrev}
                >
                  <i className="feather-chevron-left" />
                </HeroArrow>
                <HeroArrow
                  type={"button"}
                  $side={"right"}
                  aria-label={"Volgende foto"}
                  onClick={showHeroNext}
                >
                  <i className="feather-chevron-right" />
                </HeroArrow>
              </>
            )}
          </LightboxImageWrap>
        </Modal>
      )}

      {!!use_cases?.length && !!activeUseCase && (
        <IllustratedSection>
          <StyleElement
            src={"/assets/images/illustrations/illustratie-18.svg"}
            alt={""}
            right={-14}
            bottom={-20}
          />
          <div className={"container"}>
            <SectionTitleCenter>{use_cases_title}</SectionTitleCenter>
            <TabSelectWrapper>
              <TabSelect
                value={activeUseCaseId}
                onChange={(event) => setActiveUseCaseId(event.target.value)}
              >
                {use_cases.map((useCase) => (
                  <option key={useCase.id} value={useCase.id}>
                    {useCase.tab}
                  </option>
                ))}
              </TabSelect>
              <TabSelectIcon className={"feather-chevron-down"} />
            </TabSelectWrapper>
            <TabRow>
              {use_cases.map((useCase) => (
                <Tab
                  key={useCase.id}
                  $active={useCase.id === activeUseCaseId}
                  onClick={() => setActiveUseCaseId(useCase.id)}
                >
                  <span>{useCase.tab}</span>
                  {useCase.id === activeUseCaseId && <TabIndicator />}
                </Tab>
              ))}
            </TabRow>
            <Panel>
              <PanelGrid $hasImage={!!panelImages.length}>
                <div>
                  <PanelTitle>{activeUseCase.title}</PanelTitle>
                  {!!activeUseCase.description && (
                    <PanelText>{activeUseCase.description}</PanelText>
                  )}
                  {!!activeUseCase.facts?.length && (
                    <FactsRow $count={activeUseCase.facts.length}>
                      {activeUseCase.facts.map((fact, index) => (
                        <FactCell key={index}>
                          <FactCellLabel>{fact.label}</FactCellLabel>
                          <FactCellValue $highlight={fact.highlight}>
                            {fact.value}
                          </FactCellValue>
                        </FactCell>
                      ))}
                    </FactsRow>
                  )}
                  <LinesList>
                    {activeUseCase.lines?.map((line, index) => (
                      <LineText key={index}>{line}</LineText>
                    ))}
                  </LinesList>
                  {activeUseCase.groups?.map((group, index) => (
                    <Group key={index}>
                      <GroupTitle>{group.title}</GroupTitle>
                      <GroupList>
                        {group.items?.map((item, itemIndex) => (
                          <GroupItem key={itemIndex}>
                            <GroupBullet />
                            <span>{item}</span>
                          </GroupItem>
                        ))}
                      </GroupList>
                    </Group>
                  ))}
                  {!!activeUseCase.note && (
                    <PanelNote>{activeUseCase.note}</PanelNote>
                  )}
                  {!!activeUseCase.cta && (
                    <CtaWrap>
                      {activeUseCase.cta.action === "book" ? (
                        <StyledButton type="primary" onClick={onBook}>
                          {activeUseCase.cta.label}
                        </StyledButton>
                      ) : (
                        <LinkButton to={contact} small type={"primary"}>
                          {activeUseCase.cta.label}
                        </LinkButton>
                      )}
                    </CtaWrap>
                  )}
                </div>
                {!!panelImages.length && (
                  <PanelMedia>
                    <PanelImageWrap $fit={activeUseCase.image_fit}>
                      <PanelImage
                        src={panelImages[activePanelImage]?.image}
                        alt={panelImages[activePanelImage]?.alt}
                        $fit={activeUseCase.image_fit}
                      />
                      {panelImages.length > 1 && (
                        <>
                          <HeroArrow
                            type={"button"}
                            $side={"left"}
                            aria-label={"Vorige afbeelding"}
                            onClick={showPanelPrev}
                          >
                            <i className="feather-chevron-left" />
                          </HeroArrow>
                          <HeroArrow
                            type={"button"}
                            $side={"right"}
                            aria-label={"Volgende afbeelding"}
                            onClick={showPanelNext}
                          >
                            <i className="feather-chevron-right" />
                          </HeroArrow>
                        </>
                      )}
                    </PanelImageWrap>
                    {panelImages.length > 1 && (
                      <CarouselDots>
                        {panelImages.map((_, index) => (
                          <CarouselDot
                            key={index}
                            type={"button"}
                            aria-label={`Afbeelding ${index + 1}`}
                            $active={index === activePanelImage}
                            onClick={() => setActivePanelImage(index)}
                          />
                        ))}
                      </CarouselDots>
                    )}
                  </PanelMedia>
                )}
              </PanelGrid>
            </Panel>
          </div>
        </IllustratedSection>
      )}

      {!!usps?.length && (
        <Section>
          <div className={"container"}>
            <SectionTitleCenter>
              Wat deze locatie anders maakt
            </SectionTitleCenter>
            <UspSlider
              slidesPerView={1.15}
              spaceBetween={16}
              pagination={{ clickable: true }}
            >
              {usps.map((usp, index) => (
                <SwiperSlide key={index}>
                  <UspTile>
                    <UspNumber>{usp.number}</UspNumber>
                    <UspTitle>{usp.title}</UspTitle>
                    <UspText>{usp.description}</UspText>
                  </UspTile>
                </SwiperSlide>
              ))}
            </UspSlider>
            <UspGrid>
              {usps.map((usp, index) => (
                <UspTile key={index}>
                  <UspNumber>{usp.number}</UspNumber>
                  <UspTitle>{usp.title}</UspTitle>
                  <UspText>{usp.description}</UspText>
                </UspTile>
              ))}
            </UspGrid>
          </div>
        </Section>
      )}

      {!!availability_cta && (
        <AvailabilitySection>
          <div className={"container"}>
            <AvailabilityBanner>
              <div>
                <AvailabilityTitle>{availability_cta.title}</AvailabilityTitle>
                <AvailabilityText>
                  {availability_cta.description}
                </AvailabilityText>
                <ButtonRow>
                  {!!availability_cta.primary_cta && (
                    <StyledButton type="primary" onClick={onBook}>
                      {availability_cta.primary_cta}
                    </StyledButton>
                  )}
                  {!!availability_cta.secondary_cta && (
                    <CreamOutlineLink to={contact} type={"outlined"}>
                      {availability_cta.secondary_cta}
                    </CreamOutlineLink>
                  )}
                </ButtonRow>
                {!!availability_cta.footnote_prefix && (
                  <Footnote>
                    {availability_cta.footnote_prefix}
                    {!!availability_cta.email && (
                      <EmailHighlight>{availability_cta.email}</EmailHighlight>
                    )}
                  </Footnote>
                )}
              </div>
              {!!availability_cta.image && (
                <AvailabilityImageWrap>
                  <AvailabilityImage
                    src={availability_cta.image}
                    alt={availability_cta.image_alt}
                  />
                </AvailabilityImageWrap>
              )}
            </AvailabilityBanner>
          </div>
        </AvailabilitySection>
      )}
    </>
  );
};

const Marquee = ({ words }) => {
  const copy = [...words, ...words];

  const renderCopy = (keyPrefix) => (
    <MarqueeHalf>
      {copy.map((word, index) => (
        <React.Fragment key={`${keyPrefix}-${index}`}>
          <span>{word}</span>
          <Dot>·</Dot>
        </React.Fragment>
      ))}
    </MarqueeHalf>
  );

  return (
    <MarqueeWrap>
      <MarqueeTrack>
        {renderCopy("a")}
        {renderCopy("b")}
      </MarqueeTrack>
    </MarqueeWrap>
  );
};

const sectionPadding = css`
  padding: 32px 0 36px;

  @media ${device.lg} {
    padding: 56px 0 60px;
  }
`;

const Section = styled.div`
  ${sectionPadding};
  overflow: hidden;
`;

// De decoratieve illustratie staat absoluut gepositioneerd in de sectie en zou
// anders over de kaart heen vallen.
const IllustratedSection = styled(Section)`
  position: relative;

  > .container {
    position: relative;
    z-index: 1;
  }
`;

// 66px/140px = HomeNavbar's rendered height (mobile/desktop) so hero+marquee
// exactly fills the remaining viewport on load, not measured/live.
const HeroWithMarquee = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 66px);
  min-height: calc(100dvh - 66px);

  @media ${device.lg} {
    min-height: calc(100vh - 140px);
    min-height: calc(100dvh - 140px);
  }
`;

const HeroSection = styled.div`
  ${sectionPadding};

  @media ${device.lg} {
    padding: 40px 0 62px;
  }

  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
    `}
`;

const HeroGrid = styled.div`
  display: grid;
  gap: 28px;

  @media ${device.lg} {
    grid-template-columns: minmax(0, 1.02fr) minmax(0, 1fr);
    gap: 52px;
    align-items: stretch;
  }
`;

const Kicker = styled.div`
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.secondary};
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  line-height: 1.15;
  font-weight: 800;
  text-transform: uppercase;
  color: ${colors.header};
  margin: 0 0 18px;

  @media ${device.lg} {
    font-size: 38px;
    line-height: 1.12;
    max-width: 22ch;
  }
`;

const Highlight = styled.span`
  color: ${colors.secondary};
`;

const Intro = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: ${colors.text};
  margin: 0 0 14px;

  @media ${device.lg} {
    font-size: 16px;
    max-width: 56ch;
  }
`;

const FactsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 24px;

  @media ${device.lg} {
    gap: 12px;
    margin-bottom: 30px;
  }
`;

const FactTile = styled.div`
  background: ${GLASS};
  padding: 12px 10px;
  border-radius: 6px;

  @media ${device.lg} {
    padding: 17px 15px;
  }
`;

const FactValue = styled.div`
  font-size: clamp(12px, 3.2vw, 16px);
  font-weight: 800;
  line-height: 1.1;
  color: ${colors.header};
  white-space: nowrap;

  @media ${device.lg} {
    font-size: clamp(14px, 1.3vw, 19px);
  }
`;

const FactLabel = styled.div`
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${MUTED};
  margin-top: 6px;

  @media ${device.lg} {
    font-size: 10.5px;
    letter-spacing: 0.14em;
    margin-top: 8px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const HeroMedia = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MainImageWrap = styled.div`
  position: relative;
  height: 260px;

  @media ${device.lg} {
    height: 420px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 6px;
`;

const ZoomButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(4, 36, 16, 0.55);
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.secondary};
  }

  i {
    font-size: 16px;
  }

  @media ${device.lg} {
    width: 40px;
    height: 40px;

    i {
      font-size: 18px;
    }
  }
`;

const LightboxImageWrap = styled.div`
  position: relative;
`;

const LightboxImage = styled.img`
  width: 100%;
  max-height: 80vh;
  object-fit: contain;
  display: block;
  border-radius: 6px;
`;

const HeroArrow = styled.button`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === "left" ? "left: 12px;" : "right: 12px;")}
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(4, 36, 16, 0.55);
  color: #fff;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.secondary};
  }

  i {
    font-size: 18px;
  }

  @media ${device.lg} {
    width: 44px;
    height: 44px;

    i {
      font-size: 22px;
    }
  }
`;

const ThumbGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  flex: none;
`;

const ThumbButton = styled.button`
  padding: 0;
  border: 2px solid
    ${({ $active }) => ($active ? colors.secondary : "transparent")};
  border-radius: 4px;
  background: none;
  cursor: pointer;
  line-height: 0;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${colors.secondary};
  }
`;

const Thumb = styled.img`
  width: 100%;
  height: 78px;
  object-fit: cover;
  display: block;
  border-radius: 2px;

  @media ${device.lg} {
    height: 104px;
  }
`;

const marqueeAnimation = keyframes`
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(-50%);
    }
`;

const MarqueeWrap = styled.div`
  flex: none;
  background: ${colors.header};
  padding: 14px 0;
  overflow: hidden;

  @media ${device.lg} {
    padding: 18px 0;
  }
`;

const MarqueeTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${marqueeAnimation} 34s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MarqueeHalf = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.light};
  white-space: nowrap;

  @media ${device.lg} {
    font-size: 19px;
  }
`;

const Dot = styled.span`
  margin: 0 14px;
  opacity: 0.85;

  @media ${device.lg} {
    margin: 0 22px;
  }
`;

const SectionTitleCenter = styled.h2`
  font-size: 24px;
  line-height: 1.1;
  font-weight: 800;
  text-transform: uppercase;
  color: ${colors.header};
  text-align: center;
  margin: 0 0 24px;

  @media ${device.lg} {
    font-size: 32px;
    max-width: 24ch;
    margin: 0 auto 32px;
  }
`;

const UspSlider = styled(Swiper)`
  padding-bottom: 40px;

  .swiper-wrapper {
    align-items: stretch;
  }

  .swiper-slide {
    height: auto;
  }

  .swiper-pagination-bullets {
    bottom: 0;
  }

  .swiper-pagination-bullet {
    width: 8px;
    height: 8px;
    background: rgba(30, 92, 50, 0.25);
    opacity: 1;
  }

  .swiper-pagination-bullet-active {
    background: ${colors.secondary};
  }

  @media ${device.md} {
    display: none;
  }
`;

const UspGrid = styled.div`
  display: none;

  @media ${device.md} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  @media ${device.lg} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const UspTile = styled.div`
  background: ${GLASS};
  padding: 26px 20px 28px;
  border-radius: 6px;
  border-left: 3px solid transparent;
  transition: all 0.15s ease;
  height: 100%;

  &:hover {
    background: ${GLASS_HOVER};
    border-left: 3px solid ${colors.secondary};
  }

  @media ${device.lg} {
    padding: 34px 24px 36px;
  }
`;

const UspNumber = styled.div`
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.header};
  margin-bottom: 12px;
`;

const UspTitle = styled.h3`
  font-size: 15px;
  line-height: 1.25;
  font-weight: 700;
  color: ${colors.header};
  margin: 0 0 8px;
`;

const UspText = styled.p`
  font-size: 13px;
  line-height: 1.55;
  color: ${colors.primary};
  margin: 0;
`;

const TabSelectWrapper = styled.div`
  position: relative;
  margin-bottom: 24px;

  @media ${device.md} {
    display: none;
  }
`;

const TabSelect = styled.select`
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  background: ${GLASS_TAB};
  border: none;
  border-radius: 6px;
  padding: 14px 40px 14px 16px;
  font-size: 13.5px;
  font-weight: 600;
  color: ${colors.header};
  cursor: pointer;
`;

const TabSelectIcon = styled.i`
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  color: ${colors.header};
  pointer-events: none;
`;

const TabRow = styled.div`
  display: none;
  gap: 8px;

  @media ${device.md} {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const Tab = styled.div`
  position: relative;
  cursor: pointer;
  flex: none;
  background: ${({ $active }) => ($active ? GLASS_TAB_HOVER : GLASS_TAB)};
  padding: 13px 18px 11px;
  border-radius: 6px 6px 0 0;
  white-space: nowrap;
  transition: background 0.15s ease;

  &:hover {
    background: ${GLASS_TAB_HOVER};
  }

  span {
    font-size: 13px;
    font-weight: 600;
    color: ${colors.header};
  }

  @media ${device.lg} {
    padding: 15px 22px 13px;

    span {
      font-size: 13.5px;
    }
  }
`;

const TabIndicator = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: ${colors.secondary};
`;

const Panel = styled.div`
  background: ${GLASS};
  padding: 24px 20px;
  border-radius: 0 6px 6px 6px;

  @media ${device.lg} {
    padding: 32px 34px;
    min-height: 480px;
  }
`;

const PanelGrid = styled.div`
  display: grid;
  gap: 28px;

  @media ${device.lg} {
    grid-template-columns: ${({ $hasImage }) =>
      $hasImage ? "minmax(0, 1fr) 440px" : "minmax(0, 1fr)"};
    gap: 40px;
    align-items: stretch;
  }
`;

const PanelTitle = styled.h3`
  font-size: 19px;
  line-height: 1.14;
  font-weight: 800;
  text-transform: uppercase;
  color: ${colors.header};
  margin: 0 0 12px;

  @media ${device.lg} {
    font-size: 23px;
  }
`;

const PanelText = styled.p`
  font-size: 14px;
  line-height: 1.62;
  color: ${colors.text};
  margin: 0 0 20px;

  @media ${device.lg} {
    font-size: 14.5px;
    max-width: 58ch;
  }
`;

const FactsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $count }) => $count || 3}, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 20px;

  @media ${device.lg} {
    gap: 12px;
  }
`;

const FactCell = styled.div`
  background: ${GLASS};
  padding: 12px 12px;
  border-radius: 6px;

  @media ${device.lg} {
    padding: 15px 16px;
  }
`;

const FactCellLabel = styled.div`
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${MUTED};
  margin-bottom: 8px;

  @media ${device.lg} {
    font-size: 10.5px;
    letter-spacing: 0.14em;
  }
`;

const FactCellValue = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ $highlight }) => ($highlight ? colors.secondary : colors.header)};

  @media ${device.lg} {
    font-size: 14.5px;
  }
`;

const LinesList = styled.div`
  display: grid;
  gap: 10px;
  justify-items: start;

  &:not(:empty) {
    margin-bottom: 20px;
  }
`;

const Group = styled.div`
  margin-bottom: 20px;
`;

const GroupTitle = styled.h4`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.header};
  margin: 0 0 10px;

  @media ${device.lg} {
    font-size: 11.5px;
  }
`;

const GroupList = styled.div`
  display: grid;
  gap: 8px;
`;

const GroupItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;

  span {
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.text};

    @media ${device.lg} {
      font-size: 13.5px;
    }
  }
`;

const GroupBullet = styled.span`
  flex: none;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${colors.secondary};
  transform: translateY(-2px);
`;

const PanelNote = styled.p`
  font-size: 13px;
  line-height: 1.55;
  color: ${MUTED};
  margin: 0 0 20px;

  @media ${device.lg} {
    font-size: 13.5px;
    max-width: 58ch;
  }
`;

const CtaWrap = styled.div`
  width: fit-content;
  max-width: 320px;
`;

const LineText = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.text};

  @media ${device.lg} {
    font-size: 13.5px;
  }
`;

const PanelMedia = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PanelImageWrap = styled.div`
  position: relative;
  height: 230px;
  border-radius: 6px;
  overflow: hidden;
  background: ${({ $fit }) =>
    $fit === "contain" ? "rgba(255, 252, 245, .75)" : "transparent"};

  @media ${device.lg} {
    height: 380px;
  }
`;

const PanelImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${({ $fit }) => ($fit === "contain" ? "contain" : "cover")};
  display: block;
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const CarouselDot = styled.button`
  width: 10px;
  height: 10px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${({ $active }) =>
    $active ? colors.secondary : "rgba(30,92,50,.25)"};
  cursor: pointer;
  transition: background 0.15s ease;
`;

const AvailabilitySection = styled(Section)`
  overflow: visible;
  padding-bottom: 56px;

  @media ${device.lg} {
    padding-bottom: 88px;
  }
`;

const AvailabilityBanner = styled.div`
  background: ${colors.header};
  border-radius: 16px;
  padding: 40px 24px;
  box-shadow: 0 20px 40px rgba(4, 36, 16, 0.25);
  overflow: hidden;
  display: grid;
  gap: 28px;
  align-items: stretch;

  @media ${device.lg} {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    gap: 52px;
    padding: 72px 56px;
  }
`;

const AvailabilityTitle = styled.h2`
  font-size: 24px;
  line-height: 1.15;
  font-weight: 800;
  text-transform: uppercase;
  color: ${colors.light};
  margin: 0 0 16px;

  @media ${device.lg} {
    font-size: 34px;
    line-height: 1.08;
  }
`;

const AvailabilityText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: rgba(241, 232, 216, 0.85);
  margin: 0 0 22px;

  @media ${device.lg} {
    font-size: 15.5px;
    line-height: 1.65;
    max-width: 52ch;
    margin: 0 0 26px;
  }
`;

const CreamOutlineLink = styled(LinkButton).attrs({ small: true })`
  background: transparent;
  border: 1px solid rgba(241, 232, 216, 0.5);
  color: ${colors.light};

  &:hover,
  &:active,
  &:focus {
    background: rgba(241, 232, 216, 0.15);
    color: ${colors.light};
    border: 1px solid rgba(241, 232, 216, 0.5);
  }
`;

const Footnote = styled.p`
  line-height: 1.6;
  color: rgba(241, 232, 216, 0.7);
  margin: 22px 0 0;

  @media ${device.lg} {
    font-size: 13px;
  }
`;

const EmailHighlight = styled.span`
  border-bottom: 1px dotted ${colors.secondaryLight};
`;

const AvailabilityImageWrap = styled.div`
  position: relative;
  min-height: 220px;
  border-radius: 10px;
  overflow: hidden;

  @media ${device.lg} {
    min-height: 320px;
  }
`;

const AvailabilityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export default ProductRedesignPage;
